import type { Response } from 'express';
import { SeoMetaPayload } from '@sbt-web/network/types';
import { isBrowser } from '@sbt-web/utils';
import {
  getCanonical,
  getMetaDescription,
  getTitle,
} from '@/client/components/SEO/ListingHeadTags/generators';
import SeoClient, { type SeoRequest } from '@/client/components/SEO/SeoClient';
import { FiltersConfigState } from '@/reducers/filtersConfig';
import { SearchState } from '@/reducers/search';
import { HADES_PATH, WEB_API_CHANNEL } from '@/shared/constants';

const buildSeoFallback = (
  search: SearchState,
  filtersStoreState: FiltersConfigState
): SeoMetaPayload => {
  return {
    type: '',
    title: getTitle(search),
    description: getMetaDescription(search),
    canonicalUrl: getCanonical(
      search,
      filtersStoreState,
      process.env.NEXT_PUBLIC_ENV_BASE_URL
    ),
    metaRobots: 'index,follow',
    // In `ListingHeading.tsx` we have a function called buildText that is used to generate the title of the page
    // It tests the truthiness of seo?.h1, so an empty string keeps it doing the same flow as before.
    h1: '',
  };
};

const seoClient = new SeoClient(
  // We have a safe fallback on the client but we don't make the call client side
  process.env.METI_BASE_URL ?? HADES_PATH,
  WEB_API_CHANNEL,
  Number.parseInt(process.env.NEXT_PUBLIC_METI_TIMEOUT_MS, 10)
);

export const getSeoDataResponseHandler = async (
  searchState: SearchState,
  filtersStoreState: FiltersConfigState,
  seoRequest: SeoRequest,
  res?: Response
): Promise<SeoMetaPayload> => {
  if (isBrowser()) {
    // When on the client, we don't need to call Meti
    return buildSeoFallback(searchState, filtersStoreState);
  } else {
    const response =
      (await seoClient.getSeoData(seoRequest, res)) ??
      buildSeoFallback(searchState, filtersStoreState);
    // Meti appears to be returning empty canonical URLs in some cases
    if (response.canonicalUrl === '') {
      const canonicalUrl = getCanonical(
        searchState,
        filtersStoreState,
        process.env.NEXT_PUBLIC_ENV_BASE_URL
      );
      return {
        ...response,
        canonicalUrl,
      };
    }
    return response;
  }
};
